import { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Loader2, FileWarning, ShieldCheck, Mail, Download, RefreshCw, Link as LinkIcon, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTenantSettings } from '../hooks/useTenantSettings';

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * Compliance Pack generator modal.
 * Triggered from the Job action menu. Renders the selector UI and posts to the API.
 * On success, opens the Pack detail dialog so the user can download / email immediately.
 */
const CompliancePackModal = ({ open, onOpenChange, jobId, jobNumber }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null); // { workers, equipment, records }
  const [selected, setSelected] = useState(new Set());
  const [name, setName] = useState('');
  const [hideExpired, setHideExpired] = useState(false);
  const [existingPacks, setExistingPacks] = useState([]);
  const [viewPackId, setViewPackId] = useState(null);

  const reset = () => {
    setData(null);
    setSelected(new Set());
    setName('');
    setHideExpired(false);
    setExistingPacks([]);
    setViewPackId(null);
  };

  const fetchExistingPacks = useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await api.get(`/jobs/${jobId}/compliance-packs`);
      setExistingPacks(res.data?.packs || []);
    } catch (err) {
      // Silent fail — existing packs are not critical
      // eslint-disable-next-line no-console
      console.warn('Failed to load existing packs', err);
    }
  }, [jobId, api]);

  const fetchPreview = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const res = await api.post(`/jobs/${jobId}/compliance-packs/preview`);
      setData(res.data);
      // Pre-tick all non-expired with documents
      const today = todayIso();
      const pre = new Set();
      (res.data.records || []).forEach((r) => {
        const isExpired = r.expiry_date && r.expiry_date < today;
        if (r.has_document && !isExpired) pre.add(r.compliance_record_id);
      });
      setSelected(pre);
      setName(`Compliance Pack for Job #${res.data.job_number || jobId.slice(0, 8)}`);
    } catch (err) {
      toast.error('Failed to load compliance records', { description: err.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  }, [jobId, api]);

  useEffect(() => {
    if (open && jobId) {
      reset();
      fetchPreview();
      fetchExistingPacks();
    }
  }, [open, jobId, fetchPreview, fetchExistingPacks]);

  const grouped = useMemo(() => {
    if (!data) return { workers: [], equipment: [] };
    const today = todayIso();
    const filterFn = (r) => !hideExpired || !r.expiry_date || r.expiry_date >= today;
    const workers = (data.workers || []).map((w) => ({
      ...w,
      records: data.records.filter((r) => r.entity_type === 'operator' && r.entity_id === w.id && filterFn(r)),
    })).filter((w) => w.records.length > 0);
    const equipment = (data.equipment || []).map((e) => ({
      ...e,
      records: data.records.filter((r) => r.entity_type === 'equipment' && r.entity_id === e.id && filterFn(r)),
    })).filter((e) => e.records.length > 0);
    return { workers, equipment };
  }, [data, hideExpired]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const allVisible = useMemo(() => {
    const ids = [];
    grouped.workers.forEach((w) => w.records.forEach((r) => ids.push(r.compliance_record_id)));
    grouped.equipment.forEach((e) => e.records.forEach((r) => ids.push(r.compliance_record_id)));
    return ids;
  }, [grouped]);

  const selectAll = () => setSelected(new Set(allVisible));
  const clearAll = () => setSelected(new Set());

  const handleGenerate = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one document');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/jobs/${jobId}/compliance-packs`, {
        name,
        item_ids: Array.from(selected),
      });
      toast.success('Pack generation started', { description: `Pack ${res.data.pack_number}` });
      setViewPackId(res.data.id);
      // refresh history list
      fetchExistingPacks();
    } catch (err) {
      toast.error('Failed to start pack generation', { description: err.response?.data?.detail || err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const today = todayIso();

  const RecordRow = ({ r }) => {
    const isExpired = r.expiry_date && r.expiry_date < today;
    const checked = selected.has(r.compliance_record_id);
    return (
      <label
        className={`flex items-center gap-3 px-2 py-1.5 rounded hover:bg-muted/40 cursor-pointer ${!r.has_document ? 'opacity-50' : ''}`}
        data-testid={`pack-record-row-${r.compliance_record_id}`}
      >
        <Checkbox
          checked={checked}
          disabled={!r.has_document}
          onCheckedChange={() => toggle(r.compliance_record_id)}
          data-testid={`pack-record-check-${r.compliance_record_id}`}
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm truncate">
            {r.compliance_type_name}
            {r.reference_number && <span className="text-muted-foreground ml-2">· {r.reference_number}</span>}
          </div>
          <div className="text-xs text-muted-foreground">
            {r.expiry_date ? `Expires ${r.expiry_date}` : 'No expiry'}
            {!r.has_document && <span className="ml-2 text-amber-500">· no document uploaded</span>}
          </div>
        </div>
        {isExpired && (
          <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30">
            Expired
          </Badge>
        )}
      </label>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[#ffd108]" />
              Generate Compliance Pack
            </DialogTitle>
            <DialogDescription>
              Select compliance documents to include for Job #{jobNumber || jobId?.slice(0, 8)}.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {existingPacks.length > 0 && (
                <div className="border rounded-md p-2 bg-muted/30">
                  <div className="text-xs font-semibold uppercase text-muted-foreground mb-1.5 px-1">
                    Existing packs ({existingPacks.length})
                  </div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {existingPacks.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setViewPackId(p.id)}
                        className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted text-sm"
                        data-testid={`existing-pack-${p.id}`}
                      >
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="flex-1 truncate">
                          <span className="font-medium">{p.pack_number}</span>
                          <span className="text-muted-foreground ml-2">· {p.included_items?.length || 0} docs</span>
                        </span>
                        <Badge
                          variant="outline"
                          className={
                            p.status === 'ready' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'
                            : p.status === 'failed' ? 'bg-red-500/10 text-red-500 border-red-500/30'
                            : 'bg-blue-500/10 text-blue-500 border-blue-500/30'
                          }
                        >
                          {p.status}
                        </Badge>
                      </button>
                    ))}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1.5 px-1">Click a pack to view, download or email it.</div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3 border-b pb-3">
                <Button size="sm" variant="ghost" onClick={selectAll} data-testid="pack-select-all-btn">Select all</Button>
                <Button size="sm" variant="ghost" onClick={clearAll} data-testid="pack-clear-btn">Clear</Button>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={hideExpired} onCheckedChange={(v) => setHideExpired(!!v)} data-testid="pack-hide-expired" />
                  Hide expired
                </label>
                <span className="ml-auto text-xs text-muted-foreground">
                  {selected.size} of {allVisible.length} selected
                </span>
              </div>

              <ScrollArea className="h-80 pr-2">
                {grouped.workers.length === 0 && grouped.equipment.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <FileWarning className="h-10 w-10 text-muted-foreground/40 mb-2" />
                    <p className="text-sm text-muted-foreground">No compliance documents available for the workers and assets on this job.</p>
                  </div>
                )}
                {grouped.workers.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                      Workers ({grouped.workers.length})
                    </div>
                    {grouped.workers.map((w) => (
                      <div key={w.id} className="mb-2">
                        <div className="text-sm font-medium px-1">{w.name}</div>
                        {w.records.map((r) => (
                          <RecordRow key={r.compliance_record_id} r={r} />
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                {grouped.equipment.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                      Assets ({grouped.equipment.length})
                    </div>
                    {grouped.equipment.map((e) => (
                      <div key={e.id} className="mb-2">
                        <div className="text-sm font-medium px-1">{e.name}</div>
                        {e.records.map((r) => (
                          <RecordRow key={r.compliance_record_id} r={r} />
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              <div>
                <Label className="text-sm">Pack name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="pack-name-input" />
              </div>
            </>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              onClick={handleGenerate}
              disabled={submitting || selected.size === 0}
              className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
              data-testid="pack-generate-btn"
            >
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
              Generate ({selected.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {viewPackId && (
        <CompliancePackDetail
          packId={viewPackId}
          open={!!viewPackId}
          onOpenChange={(o) => { if (!o) { setViewPackId(null); fetchExistingPacks(); } }}
        />
      )}
    </>
  );
};

/**
 * Pack detail dialog — shows build status, download button, email panel, activity.
 * Polls every 2s while pack is in queued/building state.
 */
export const CompliancePackDetail = ({ packId, open, onOpenChange }) => {
  const { api } = useAuth();
  const { fmtDate, fmtDateTime } = useTenantSettings();
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailing, setEmailing] = useState(false);

  const fetchPack = useCallback(async () => {
    if (!packId) return;
    try {
      const res = await api.get(`/compliance-packs/${packId}`);
      setPack(res.data);
    } catch (err) {
      toast.error('Failed to load pack', { description: err.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  }, [packId]);

  useEffect(() => {
    if (open && packId) {
      setLoading(true);
      fetchPack();
    }
  }, [open, packId, fetchPack]);

  // Poll while building
  useEffect(() => {
    if (!open || !pack) return;
    if (!['queued', 'building'].includes(pack.status)) return;
    const t = setInterval(fetchPack, 2000);
    return () => clearInterval(t);
  }, [open, pack, fetchPack]);

  const handleDownload = async () => {
    try {
      const res = await api.get(`/compliance-packs/${packId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pack.pack_number || 'compliance-pack'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed', { description: err.response?.data?.detail || err.message });
    }
  };

  const handleEmail = async () => {
    const list = emailRecipients
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (list.length === 0) {
      toast.error('Enter at least one recipient');
      return;
    }
    if (list.length > 10) {
      toast.error('Maximum 10 recipients per send');
      return;
    }
    setEmailing(true);
    try {
      await api.post(`/compliance-packs/${packId}/email`, {
        recipients: list,
        message: emailMessage || undefined,
      });
      toast.success(`Pack emailed to ${list.length} recipient(s)`);
      setEmailRecipients('');
      setEmailMessage('');
      fetchPack();
    } catch (err) {
      toast.error('Email failed', { description: err.response?.data?.detail || err.message });
    } finally {
      setEmailing(false);
    }
  };

  const handleRegenerate = async () => {
    try {
      await api.post(`/compliance-packs/${packId}/regenerate`);
      toast.info('Regeneration started');
      fetchPack();
    } catch (err) {
      toast.error('Regenerate failed', { description: err.response?.data?.detail || err.message });
    }
  };

  const handleReissueLink = async () => {
    try {
      await api.post(`/compliance-packs/${packId}/reissue-link`);
      toast.success('Share link reissued');
      fetchPack();
    } catch (err) {
      toast.error('Reissue failed', { description: err.response?.data?.detail || err.message });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#ffd108]" />
            {pack?.pack_number || 'Compliance Pack'}
          </DialogTitle>
          <DialogDescription>{pack?.name}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : pack ? (
          <>
            <div className="flex items-center gap-2 text-sm">
              <span>Status:</span>
              <Badge variant="outline" className={
                pack.status === 'ready' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'
                : pack.status === 'failed' ? 'bg-red-500/10 text-red-500 border-red-500/30'
                : 'bg-blue-500/10 text-blue-500 border-blue-500/30'
              }>
                {['queued', 'building'].includes(pack.status) && <Loader2 className="h-3 w-3 mr-1 animate-spin inline" />}
                {pack.status}
              </Badge>
              {pack.page_count && <span className="text-muted-foreground">· {pack.page_count} pages</span>}
              {pack.pdf_size_bytes && <span className="text-muted-foreground">· {(pack.pdf_size_bytes / 1024 / 1024).toFixed(2)} MB</span>}
            </div>

            {pack.status === 'failed' && pack.build_error && (
              <div className="text-sm text-red-500 bg-red-500/10 p-3 rounded">
                {pack.build_error}
              </div>
            )}

            {pack.status === 'ready' && (
              <>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={handleDownload} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]" data-testid="pack-download-btn">
                    <Download className="mr-2 h-4 w-4" /> Download PDF
                  </Button>
                  <Button variant="outline" onClick={handleRegenerate} data-testid="pack-regenerate-btn">
                    <RefreshCw className="mr-2 h-4 w-4" /> Regenerate
                  </Button>
                  <Button variant="outline" onClick={handleReissueLink} data-testid="pack-reissue-btn">
                    <LinkIcon className="mr-2 h-4 w-4" /> Reissue share link
                  </Button>
                </div>

                <div className="border-t pt-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Send to customer (max 10 recipients)</span>
                  </div>
                  <Input
                    placeholder="email1@x.com, email2@y.com"
                    value={emailRecipients}
                    onChange={(e) => setEmailRecipients(e.target.value)}
                    data-testid="pack-email-recipients"
                  />
                  <Input
                    placeholder="Optional message"
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    data-testid="pack-email-message"
                  />
                  <Button onClick={handleEmail} disabled={emailing} data-testid="pack-email-send">
                    {emailing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                    Send
                  </Button>
                  <div className="text-xs text-muted-foreground">
                    Share link expires {pack.share_expires_at ? fmtDate(pack.share_expires_at) : '—'}
                  </div>
                </div>

                {(pack.emailed_to || []).length > 0 && (
                  <div className="border-t pt-3">
                    <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">Sent to</div>
                    <ul className="space-y-1 text-sm">
                      {pack.emailed_to.map((s, i) => (
                        <li key={i} className="flex items-center justify-between">
                          <span>{s.email}</span>
                          <span className="text-xs text-muted-foreground">{fmtDateTime(s.sent_at)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="border-t pt-3">
                  <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">Downloads</div>
                  <div className="text-sm">{pack.download_count || 0} downloads</div>
                </div>
              </>
            )}
          </>
        ) : (
          <div className="text-sm text-muted-foreground">Pack not found.</div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CompliancePackModal;
